import {
    type FC
} from "react";
import {
    View
} from "react-native";
import type IPaginationLoadMoreProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePaginationContext
} from "../../context";
import {
    CircleAlertIcon
} from "lucide-react-native";
import {
    formatNumber
} from "../../../../helpers/locale/format";
import Button from "../../../button";
import Text from "../../../text";

const PaginationLoadMore: FC<IPaginationLoadMoreProps> = ({
    customTheme: customThemeProp,
    style
}) => {
    const context = usePaginationContext();

    const customTheme = customThemeProp ?? context.customTheme;

    const {
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const {
        container: containerDynamicStyle,
        error: errorDynamicStyle
    } = useStyles({
        spaces
    });

    const loadedCount = context.loadedCount ?? 0;

    const unitTitle = context.unitTitle ?? localize("pagination-unit");

    const isAllLoaded = context.totalItems !== undefined && loadedCount >= context.totalItems;

    const getCountText = () => {
        const loadedText = formatNumber({
            locale: activeLocale,
            value: loadedCount
        });

        if(context.totalItems === undefined) {
            return localize("pagination-loaded-only", [
                loadedText,
                unitTitle
            ]);
        }

        const totalText = formatNumber({
            value: context.totalItems,
            locale: activeLocale
        });

        if(isAllLoaded) {
            return localize("pagination-all-loaded", [
                totalText,
                totalText,
                unitTitle
            ]);
        }

        return localize("pagination-loaded-count", [
            loadedText,
            totalText,
            unitTitle
        ]);
    };

    const handleLoadMore = () => {
        context.onLoadMore?.();
    };

    const renderStatus = () => {
        if(context.isError) {
            return <View
                style={[
                    stylesheet.error,
                    errorDynamicStyle
                ]}
                role="alert"
            >
                <CircleAlertIcon
                    color={colors.content.text.danger}
                    size={iconSizes.small}
                />
                <Text
                    customColor={colors.content.text.danger}
                    customTheme={customTheme}
                    variant="bodySmallSize"
                >
                    {context.errorTitle ?? localize("pagination-load-error")}
                </Text>
            </View>;
        }

        return <Text
            customColor={colors.content.text.mid}
            customTheme={customTheme}
            style={stylesheet.count}
            variant="bodySmallSize"
            aria-live="polite"
            role="status"
        >
            {getCountText()}
        </Text>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderStatus()}
        {
            isAllLoaded ?
                null
            :
                <Button
                    title={context.isError ? localize("try-again") : localize("pagination-load-more")}
                    spreadBehaviour={context.isCompact ? "stretch" : "free"}
                    isLoading={context.isLoading}
                    customTheme={customTheme}
                    onPress={handleLoadMore}
                    size={context.size}
                    variant="outline"
                    type="neutral"
                />
        }
    </View>;
};
export default PaginationLoadMore;
